import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  createBreakoutReport,
  getBreakoutReports,
  getCurrentUserGroupId,
  updateBreakoutReport,
} from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';

import { Button, Input, Modal, Spin, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import BreakoutReport from './BreakoutReport';

import PropTypes from 'prop-types';

import styles from '../Messages.module.scss';

const { TextArea } = Input;

const RENDER = {
  INFO: 'INFO',
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
};

const MAX_LENGTH_REPORT = 2048;

const BreakoutReportModal = ({ ...props }) => {
  const [render, setRender] = useState(RENDER.INFO);
  const [content, setContent] = useState('');

  const dispatch = useDispatch();

  const { selectedUser, currentUserGroupId, selectedChatTopic, isLoading, breakoutReports } = useSelector(selectChat);
  const { t } = useTranslation('chat');

  useEffect(() => {
    dispatch(getCurrentUserGroupId(selectedUser.chatId));
  }, [selectedUser?.chatId]);

  useEffect(() => {
    dispatch(getBreakoutReports(selectedChatTopic.chatId));
  }, [selectedChatTopic?.chatId]);

  useEffect(() => {
    setRender(RENDER.INFO);
    setContent('');
  }, [props.open]);

  const reportIds = useMemo(() => breakoutReports?.map(report => report.groupId), [breakoutReports]);

  const handleEditClick = reportContent => {
    setRender(RENDER.UPDATE);
    setContent(reportContent);
  };

  const sendResult = () => {
    dispatch(createBreakoutReport(selectedChatTopic.chatId, content, () => setRender(RENDER.INFO)));
  };

  const updateResult = () => {
    dispatch(
      updateBreakoutReport(selectedChatTopic.chatId, content, () => {
        setRender(RENDER.INFO);
        dispatch(getBreakoutReports(selectedChatTopic.chatId));
      })
    );
  };

  const renderBlock = () => {
    switch (render) {
      case RENDER.INFO:
        if (!isLoading.getBreakoutReports && !breakoutReports.length) {
          return (
            <div className={styles['breakout-report__no-result']}>
              <Typography.Text>{t('notHaveResults')}</Typography.Text>
            </div>
          );
        }
        return (
          <Spin spinning={isLoading.getBreakoutReports} indicator={<LoadingOutlined spin />}>
            <div className={styles['breakout-report__list']}>
              {breakoutReports.map((report, index) => (
                <BreakoutReport
                  canEdit={report.groupId === currentUserGroupId}
                  onEdit={handleEditClick}
                  key={index}
                  data={report}
                />
              ))}
            </div>
          </Spin>
        );

      case RENDER.UPDATE:
      case RENDER.CREATE:
        return (
          <div className={styles['breakout-report__create']}>
            <TextArea
              count={{ show: true, max: MAX_LENGTH_REPORT }}
              rows={8}
              onChange={e => setContent(e.target.value)}
              value={content}
            />
            <div className={styles['breakout-report__buttons']}>
              <Button onClick={() => setRender(RENDER.INFO)}>{t('cancel')}</Button>
              <Button
                disabled={!content.trim().length || content.length > MAX_LENGTH_REPORT}
                onClick={render === RENDER.UPDATE ? updateResult : sendResult}
                type='primary'
              >
                {t(render === RENDER.UPDATE ? 'edit' : 'send')}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <Modal {...props} title={t('report')} width={700} footer={null}>
      <div className={styles['breakout-report__container']}>
        {renderBlock()}
        {currentUserGroupId &&
          !reportIds.includes(currentUserGroupId) &&
          render !== RENDER.CREATE &&
          !isLoading.getBreakoutReports && (
            <Button onClick={() => setRender(RENDER.CREATE)} type='primary'>
              {t('addResults')}
            </Button>
          )}
      </div>
    </Modal>
  );
};

BreakoutReportModal.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
};

export default BreakoutReportModal;
